import {
  acquireHubLock,
  releaseHubLock,
  renewHubLock,
} from "./contentHub.service";
import { statusOf } from "./httpStatus";
import type { HubLockConflict, HubLockRequest, HubRow } from "./hubTypes";

export interface HeldLock {
  // Stops renewing and gives the turn back. Safe to call again; a release that
  // fails is ignored, because the lease expires by itself.
  release: () => void;
}

// A run holds the whole tracker unless Filas names rows, and the courses of
// the rows it processes: two live runs never share a course. A row without a
// course ID adds none.
export function lockRequestFor(
  spreadsheet: string,
  rows: HubRow[],
  restricted: boolean,
): HubLockRequest {
  const courseIds = rows
    .map((row) => row.courseId)
    .filter((id): id is number => id !== null);
  return {
    spreadsheet,
    rows: restricted ? rows.map((row) => row.rowNumber) : null,
    courseIds: Array.from(new Set(courseIds)),
  };
}

// Takes the turn on what the request names and keeps it while the caller
// works. A refusal or any other failure to take it rejects, with nothing left
// running.
export async function takeLock(
  request: HubLockRequest,
  onLost: () => void,
): Promise<HeldLock> {
  const { token, heartbeatSeconds } = await acquireHubLock(request);
  let renewing = true;
  let released = false;

  const timer = setInterval(async () => {
    try {
      await renewHubLock(token);
    } catch (error) {
      // Only a 409 says the lease is gone; any other failure may pass by the
      // next tick. A late answer after the release says nothing either.
      if (statusOf(error) === 409 && renewing) {
        renewing = false;
        clearInterval(timer);
        onLost();
      }
    }
  }, heartbeatSeconds * 1000);

  return {
    release: () => {
      if (released) return;
      released = true;
      renewing = false;
      clearInterval(timer);
      void (async () => {
        try {
          await releaseHubLock(token);
        } catch {
          // The lease expires by itself.
        }
      })();
    },
  };
}

const clock = (iso: string): string => {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// 140, 120, 121, 122 -> "120-122, 140"
const compactRows = (rows: number[]): string => {
  const sorted = Array.from(new Set(rows)).sort((a, b) => a - b);
  const ranges: string[] = [];
  let from = 0;
  while (from < sorted.length) {
    let to = from;
    while (sorted[to + 1] === sorted[to] + 1) to += 1;
    ranges.push(
      to > from ? `${sorted[from]}-${sorted[to]}` : String(sorted[from]),
    );
    from = to + 1;
  }
  return ranges.join(", ");
};

// With a shared course the holder's rows are not the cause, so they stay out.
const heldPart = ({ reason, holderRows }: HubLockConflict): string => {
  if (reason === "courses") return "con alguno de estos cursos";
  if (holderRows === null) return "en todo este tracker";
  return `en las filas ${compactRows(holderRows)} de este tracker`;
};

const PARALLEL_HINT =
  " Para trabajar en paralelo, cada persona tiene que indicar sus filas en «Filas» sin que se crucen.";

// The 409 that says someone else holds part of what the run asked for, worded
// for the operator; null for any other failure. A run that named no rows also
// learns how to work in parallel.
export function lockRefusalMessage(
  error: unknown,
  requestedRows: number[] | null,
): string | null {
  if (statusOf(error) !== 409) return null;
  const conflict = (error as { response: { data: HubLockConflict } }).response
    .data;
  const hint = requestedRows === null ? PARALLEL_HINT : "";
  return `Ya hay una carga en curso ${heldPart(conflict)}: la inició ${conflict.holderName} a las ${clock(conflict.acquiredAt)}.${hint}`;
}
